import React from "react";
import QRCode from "qrcode";
import { Auth } from "../helpers/auth";
import { useNavigate, useSearchParams } from "react-router-dom";
import logoWhite from "../images/bossdrop_logo_transparent_512.png";

const MIN_PASSWORD_LENGTH = 12;
const SALES_EMAIL = "sales@bossdrop.ai";

// POST /auth/register answers 202 { checkEmail: true } instead of signing
// anyone in when the address is on a Team directory: it emails a link to set
// the password, and following that link is the proof the address is theirs.
// While signups are closed it answers the same way for every address, so the
// page cannot say which ones are listed.
const CHECK_EMAIL_CLOSED =
  "Check your email. If this address is on your team's Team directory, we sent you a link to set your password. If nothing arrives in a few minutes, ask your workspace admin to add you.";
const CHECK_EMAIL_OPEN = "Check your email for a link to set your password.";
// Google sign-in bounces back with ?error=google_email_unverified when Google
// has not verified the address, which then proves nothing about the mailbox.
const GOOGLE_EMAIL_UNVERIFIED =
  "Google has not verified that email address, so it cannot be used to sign in. Verify it with Google first, or sign in with your password.";

// returnTo comes straight from the URL (and from emailed links) and ends up in
// window.location.href, where a "javascript:" value would run on this origin
// with a fresh session. Only a path on this origin is ever legitimate, so
// anything else becomes "/". Resolving through URL rather than checking string
// prefixes also rejects "//host", "/\host" and tab or newline tricks like
// "/<tab>/host", which the browser would otherwise read as another site.
function safeReturnTo(value) {
  try {
    const raw = String(value || "");
    if (!raw.startsWith("/")) return "/";
    const u = new URL(raw, window.location.origin);
    if (u.origin !== window.location.origin) return "/";
    return u.pathname + u.search + u.hash;
  } catch {
    return "/";
  }
}

function useQuery() {
  const p = new URLSearchParams(window.location.search);
  return {
    returnTo: safeReturnTo(p.get("returnTo")),
    token: p.get("token") || "",
    // Invite links are reset links with a friendlier face: the account exists
    // but has never had a password, so this is "set" rather than "reset".
    invite: p.get("invite") === "1",
    // Google sign-in bounces back here with ?error=signups_closed when the
    // address has no account and signups are closed.
    urlError: p.get("error") || "",
    passwordSet: p.get("passwordSet") === "1",
  };
}

function SalesLink() {
  return (
    <a className="authLink" href={`mailto:${SALES_EMAIL}`}>
      {SALES_EMAIL}
    </a>
  );
}

// The one thing "signups_closed" means, whether it came back from
// POST /auth/register or from the Google callback's redirect.
function SignupsClosedMessage() {
  return (
    <>
      This email isn't on a BossDrop team yet. Ask your workspace admin to add
      you in Team directory, or email <SalesLink />.
    </>
  );
}

function isValidUser(u) {
  return !!(u && (u.id || u._id || u.email));
}

function buildMfaFlowFromPending(pending = {}) {
  const purpose = pending?.purpose === "setup" ? "setup" : "verify";
  const method = pending?.method || "";
  const availableMethods = Array.isArray(pending?.availableMethods)
    ? pending.availableMethods
    : [];

  if (purpose === "setup") {
    if (method === "sms") {
      return {
        active: true,
        purpose,
        step: "setup_sms_verify",
        method: "sms",
        code: "",
        phone: "",
        maskedPhone: pending?.maskedPhone || "",
        secret: "",
        otpAuthUrl: "",
        availableMethods,
      };
    }
    return {
      active: true,
      purpose,
      step: "setup_choice",
      method: "",
      code: "",
      phone: "",
      maskedPhone: "",
      secret: "",
      otpAuthUrl: "",
      availableMethods,
    };
  }

  return {
    active: true,
    purpose,
    step: "verify",
    method: method || "auth_app",
    code: "",
    phone: "",
    maskedPhone: pending?.maskedPhone || "",
    secret: "",
    otpAuthUrl: "",
    availableMethods,
  };
}

export default function SignInPage() {
  const { returnTo, token, invite, urlError, passwordSet } = useQuery();
  const [mode, setMode] = React.useState(token ? "reset" : "login");
  const [form, setForm] = React.useState({ email: "", password: "", name: "" });
  const [status, setStatus] = React.useState(
    urlError === "google_email_unverified" ? GOOGLE_EMAIL_UNVERIFIED : "",
  );
  // "signups_closed" is the one rejection a stranger is likely to hit, from
  // register or from the Google bounce (?error=signups_closed). It gets its own
  // banner at the top of the form instead of the small grey status line.
  const [closedError, setClosedError] = React.useState(
    urlError === "signups_closed",
  );
  const closedBannerRef = React.useRef(null);
  // Set when a reset or invite link is opened in a browser that already has a
  // hub session (Eric testing an invite he just sent, most likely).
  const [signedInAs, setSignedInAs] = React.useState("");
  const notice = passwordSet
    ? "Password set. Sign in with your email and new password."
    : "";
  const isInviteReset = !!token && invite;
  // Closed until the server says otherwise: while this is loading, and if the
  // call fails, the page shows the invite-only face rather than a Create
  // account tab that the server would only reject.
  const [signupsOpen, setSignupsOpen] = React.useState(false);
  const [mfaFlow, setMfaFlow] = React.useState(null);
  const [totpQrDataUrl, setTotpQrDataUrl] = React.useState("");
  const nav = useNavigate();
  const [sp] = useSearchParams();

  const onChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  React.useEffect(() => {
    document.documentElement.classList.add("auth-open");
    document.body.classList.add("auth-open");
    return () => {
      document.documentElement.classList.remove("auth-open");
      document.body.classList.remove("auth-open");
    };
  }, []);

  React.useEffect(() => {
    let dead = false;
    (async () => {
      try {
        const me = await Auth.me();
        if (isValidUser(me?.user)) {
          // A reset or invite link still has a job to do, and so does the
          // "Password set" landing after it, so neither bounces into the hub.
          if (!token && !passwordSet) {
            nav(safeReturnTo(sp.get("returnTo")), { replace: true });
            return;
          }
          if (!dead) setSignedInAs(me.user.email || "");
        }
      } catch {}

      try {
        const pending = await Auth.getPendingMfa();
        if (!dead && pending?.pending) {
          setMfaFlow(buildMfaFlowFromPending(pending.pending));
        }
      } catch {
        if (!dead) setMfaFlow(null);
      }
    })();
    return () => {
      dead = true;
    };
  }, [nav, sp, token, passwordSet]);

  // The banner sits at the top of the form and the button that triggers it sits
  // at the bottom, which on a phone can be a scroll apart.
  React.useEffect(() => {
    if (!closedError) return;
    try {
      closedBannerRef.current?.scrollIntoView({ block: "nearest" });
    } catch {}
  }, [closedError]);

  // Every mode switch goes through here so the signups_closed banner never
  // follows someone onto a form it does not belong to.
  function switchMode(next) {
    setClosedError(false);
    setMode(next);
  }

  React.useEffect(() => {
    let dead = false;
    (async () => {
      try {
        const info = await Auth.signupStatus();
        if (!dead) setSignupsOpen(info?.open === true);
      } catch {
        if (!dead) setSignupsOpen(false);
      }
    })();
    return () => {
      dead = true;
    };
  }, []);

  React.useEffect(() => {
    let canceled = false;
    const uri = String(mfaFlow?.otpAuthUrl || "").trim();

    if (mfaFlow?.step !== "setup_totp_confirm" || !uri) {
      setTotpQrDataUrl("");
      return () => {
        canceled = true;
      };
    }

    (async () => {
      try {
        const nextDataUrl = await QRCode.toDataURL(uri, {
          errorCorrectionLevel: "M",
          margin: 1,
          width: 220,
          color: {
            dark: "#3a3463",
            light: "#ffffff",
          },
        });
        if (!canceled) setTotpQrDataUrl(nextDataUrl);
      } catch (error) {
        console.warn("Failed to build MFA QR code:", error);
        if (!canceled) setTotpQrDataUrl("");
      }
    })();

    return () => {
      canceled = true;
    };
  }, [mfaFlow?.otpAuthUrl, mfaFlow?.step]);

  async function finishSignedIn() {
    try {
      const keys = Object.keys(localStorage || {});
      for (const k of keys) {
        if (k.startsWith("bd:")) localStorage.removeItem(k);
      }
    } catch {}
    window.location.href = returnTo || "/";
  }

  async function handleAuthResult(result, fallbackUser = null) {
    if (result?.requiresMfa || result?.requiresMfaSetup) {
      setMfaFlow(
        buildMfaFlowFromPending(
          result.pendingMfa || {
            purpose: result.requiresMfaSetup ? "setup" : "verify",
          },
        ),
      );
      setStatus("");
      return;
    }
    if (fallbackUser || result?.user) {
      await finishSignedIn();
    }
  }

  async function doLogin(e) {
    e.preventDefault();
    setClosedError(false);
    setStatus("...");
    try {
      const result = await Auth.login({
        email: form.email,
        password: form.password,
      });
      if (result?.requiresMfa || result?.requiresMfaSetup) {
        await handleAuthResult(result);
        return;
      }
      const me = await Auth.me();
      if (!me?.user) throw new Error("session_missing");
      await finishSignedIn();
    } catch (err) {
      setStatus(err?.data?.error || "Login failed");
    }
  }

  async function doRegister(e) {
    e.preventDefault();
    setClosedError(false);
    // While signups are closed no password is sent at all: the server only
    // ever emails a set-password link here. With signups open a brand-new
    // account still uses the one typed below.
    if (signupsOpen && (form.password || "").length < MIN_PASSWORD_LENGTH) {
      setStatus(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }
    setStatus("...");
    try {
      const result = await Auth.register({
        email: form.email,
        password: signupsOpen ? form.password : undefined,
        name: form.name,
      });

      if (result?.checkEmail) {
        setStatus(signupsOpen ? CHECK_EMAIL_OPEN : CHECK_EMAIL_CLOSED);
        return;
      }

      // Register creates no workspace and writes no settings: a new account
      // reaches a workspace only by being listed or invited.
      await handleAuthResult(result, result?.user);
      if (!result?.requiresMfa && !result?.requiresMfaSetup) {
        window.location.href = returnTo;
      }
    } catch (err) {
      const code = err?.data?.error || "";
      if (code === "signups_closed") {
        setClosedError(true);
        setStatus("");
        return;
      }
      setStatus(code || "Signup failed");
    }
  }

  async function doForgot(e) {
    e.preventDefault();
    setClosedError(false);
    setStatus("Sending reset link...");
    try {
      await Auth.requestReset({
        email: form.email,
        origin: window.location.origin,
        returnTo,
      });
      setStatus("Check your email for a reset link.");
    } catch {
      setStatus("If that email exists, we sent a link.");
    }
  }

  async function doReset(e) {
    e.preventDefault();
    if (!token) {
      setStatus("Missing token.");
      return;
    }
    if ((form.password || "").length < MIN_PASSWORD_LENGTH) {
      setStatus(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }

    setStatus(isInviteReset ? "Setting your password..." : "Resetting...");
    try {
      await Auth.reset({ token, password: form.password });
      // The server clears the session and asks for a fresh sign-in, so this
      // lands back on the login form. passwordSet=1 is what turns that into a
      // "you are done, now sign in" message instead of a bare login page.
      const next =
        `/signin?returnTo=${encodeURIComponent(returnTo || "/")}` +
        (isInviteReset ? "&passwordSet=1" : "");
      window.location.href = next;
    } catch (err) {
      const code = err?.data?.error || "";
      if (isInviteReset && code === "invalid_or_expired") {
        setStatus(
          "This invite link has expired or was already used. Use Forgot password to get a new one.",
        );
        return;
      }
      setStatus(code || "Reset failed");
    }
  }

  async function refreshPendingMfa() {
    try {
      const pending = await Auth.getPendingMfa();
      if (pending?.pending) {
        setMfaFlow(buildMfaFlowFromPending(pending.pending));
      }
    } catch {}
  }

  async function chooseTotpSetup() {
    setStatus("Preparing authenticator app setup...");
    try {
      const setup = await Auth.startTotpSetup();
      setMfaFlow((current) => ({
        ...(current || buildMfaFlowFromPending({ purpose: "setup" })),
        active: true,
        purpose: "setup",
        step: "setup_totp_confirm",
        method: "auth_app",
        code: "",
        secret: setup?.secret || "",
        otpAuthUrl: setup?.otpAuthUrl || "",
      }));
      setStatus("");
    } catch (err) {
      setStatus(err?.data?.error || "Failed to start authenticator setup");
    }
  }

  async function startSmsSetup() {
    if (!mfaFlow?.phone?.trim()) {
      setStatus("Enter a phone number first.");
      return;
    }
    setStatus("Sending code...");
    try {
      const setup = await Auth.startSmsSetup({ phone: mfaFlow.phone });
      setMfaFlow((current) => ({
        ...(current || buildMfaFlowFromPending({ purpose: "setup" })),
        active: true,
        purpose: "setup",
        step: "setup_sms_verify",
        method: "sms",
        code: "",
        maskedPhone: setup?.maskedPhone || "",
      }));
      setStatus("");
    } catch (err) {
      setStatus(err?.data?.error || "Failed to send code");
    }
  }

  async function confirmTotpSetup(e) {
    e.preventDefault();
    setStatus("Verifying...");
    try {
      await Auth.confirmTotpSetup({ code: mfaFlow?.code || "" });
      await finishSignedIn();
    } catch (err) {
      setStatus(err?.data?.error || "Invalid code");
    }
  }

  async function confirmSmsSetup(e) {
    e.preventDefault();
    setStatus("Verifying...");
    try {
      await Auth.confirmSmsSetup({ code: mfaFlow?.code || "" });
      await finishSignedIn();
    } catch (err) {
      setStatus(err?.data?.error || "Invalid code");
    }
  }

  async function verifyMfa(e) {
    e.preventDefault();
    setStatus("Verifying...");
    try {
      await Auth.verifyMfa({
        code: mfaFlow?.code || "",
        method: mfaFlow?.method || "auth_app",
      });
      await finishSignedIn();
    } catch (err) {
      setStatus(err?.data?.error || "Invalid code");
    }
  }

  async function resendMfaCode() {
    setStatus("Sending another code...");
    try {
      const result = await Auth.resendMfaCode();
      setMfaFlow((current) =>
        current
          ? {
              ...current,
              maskedPhone: result?.maskedPhone || current.maskedPhone,
            }
          : current,
      );
      await refreshPendingMfa();
      setStatus("");
    } catch (err) {
      setStatus(err?.data?.error || "Could not resend the code");
    }
  }

  async function startOver() {
    try {
      await Auth.logout();
    } catch {}
    setMfaFlow(null);
    setStatus("");
    switchMode("login");
    setForm((current) => ({ ...current, password: "" }));
  }

  const API_BASE = "https://api.bossdrop.ai";
  const GoogleBtn = () => (
    <a
      className="btn primary block large google"
      href={
        `${API_BASE}/api/google/oauth/start` +
        `?mode=signin` +
        `&origin=${encodeURIComponent(window.location.origin)}` +
        `&returnTo=${encodeURIComponent(returnTo || "/")}`
      }
    >
      <svg
        className="gLogo"
        viewBox="0 0 48 48"
        aria-hidden="true"
        focusable="false"
      >
        <path
          fill="#EA4335"
          d="M24 9.5c3.32 0 6.32 1.15 8.68 3.42l6.5-6.5C35.82 2.42 30.38 0 24 0 14.62 0 6.4 5.38 2.56 13.22l7.56 5.86C12.1 13.2 17.56 9.5 24 9.5z"
        />
        <path
          fill="#4285F4"
          d="M46.5 24.5c0-1.64-.15-3.2-.43-4.7H24v9h12.7c-.55 2.95-2.24 5.45-4.77 7.13l7.3 5.66C43.84 37.83 46.5 31.7 46.5 24.5z"
        />
        <path
          fill="#FBBC05"
          d="M10.12 28.08A14.48 14.48 0 0 1 9.5 24c0-1.42.24-2.78.67-4.08l-7.6-5.9A23.9 23.9 0 0 0 0 24c0 3.9.94 7.58 2.6 10.82l7.52-6.74z"
        />
        <path
          fill="#34A853"
          d="M24 48c6.38 0 11.74-2.1 15.66-5.72l-7.3-5.66c-2.04 1.37-4.67 2.18-8.36 2.18-6.44 0-11.9-3.7-13.88-8.58l-7.56 5.9C6.4 42.62 14.62 48 24 48z"
        />
      </svg>
      Continue with Google
    </a>
  );

  const showMfa = !!mfaFlow?.active;
  // With signups closed there is no "Create account" tab to show where you
  // are, so the header carries the mode instead.
  const closedRegister = mode === "register" && !signupsOpen;
  const closedBanner = closedError ? (
    <div
      ref={closedBannerRef}
      className="infoBanner authNotice authNotice--warn"
      role="alert"
    >
      <SignupsClosedMessage />
    </div>
  ) : null;
  const canUseSms =
    Array.isArray(mfaFlow?.availableMethods) &&
    mfaFlow.availableMethods.some((item) => item?.value === "sms");

  return (
    <div
      className="authGate"
      style={{
        display: "grid",
        placeItems: "center",
      }}
    >
      <div className="authWrap">
        <div className="panel authCard">
          <div className="authHeader">
            <div className="brandMark" aria-hidden="true">
              <img src={logoWhite} alt="BossDrop" />
            </div>
            <h1 className="authTitle">
              {showMfa
                ? "Verify your sign-in"
                : isInviteReset && mode === "reset"
                  ? "Set your password"
                  : closedRegister
                    ? "Join your team"
                    : "Welcome to BossDrop"}
            </h1>
            <p className="muted">
              {showMfa
                ? "Finish the second step to get into your workspace."
                : isInviteReset && mode === "reset"
                  ? "Welcome to BossDrop. Choose a password to finish setting up your account."
                  : closedRegister
                    ? "Create your account to join your team's workspace."
                    : "Sign in to your workspace"}
            </p>
          </div>

          {!showMfa ? (
            <div className="ctaRow" role="tablist" aria-label="auth modes">
              <button
                className={`btn ${mode === "login" ? "success" : "ghost"}`}
                onClick={() => switchMode("login")}
              >
                Sign in
              </button>
              {signupsOpen ? (
                <button
                  className={`btn ${mode === "register" ? "success" : "ghost"}`}
                  onClick={() => switchMode("register")}
                >
                  Create account
                </button>
              ) : null}
              <button
                className="btn ghost"
                onClick={() => switchMode("forgot")}
              >
                Forgot password
              </button>
            </div>
          ) : null}

          {showMfa ? (
            <div className="formGrid">
              {mfaFlow.step === "setup_choice" ? (
                <>
                  <div className="muted">
                    BossDrop now requires a second sign-in step. Pick how you
                    want to verify this account going forward.
                  </div>
                  <div className="ctaRow">
                    <button className="btn primary large" onClick={chooseTotpSetup}>
                      Use authenticator app
                    </button>
                    {canUseSms ? (
                      <button
                        className="btn ghost"
                        onClick={() =>
                          setMfaFlow((current) => ({
                            ...(current || buildMfaFlowFromPending({ purpose: "setup" })),
                            step: "setup_sms_phone",
                            method: "sms",
                            phone: "",
                            code: "",
                          }))
                        }
                      >
                        Use text message
                      </button>
                    ) : null}
                  </div>
                </>
              ) : null}

              {mfaFlow.step === "setup_sms_phone" ? (
                <>
                  <label className="field wide">
                    Mobile number
                    <input
                      className="inputFancy"
                      type="tel"
                      placeholder="+13125551212"
                      value={mfaFlow.phone || ""}
                      onChange={(e) =>
                        setMfaFlow((current) => ({
                          ...(current || buildMfaFlowFromPending({ purpose: "setup" })),
                          phone: e.target.value,
                        }))
                      }
                    />
                  </label>
                  <div className="ctaRow">
                    <button className="btn primary large" onClick={startSmsSetup}>
                      Send code
                    </button>
                    <button
                      type="button"
                      className="btn ghost"
                      onClick={() =>
                        setMfaFlow((current) => ({
                          ...(current || buildMfaFlowFromPending({ purpose: "setup" })),
                          step: "setup_choice",
                          method: "",
                          phone: "",
                        }))
                      }
                    >
                      Back
                    </button>
                  </div>
                </>
              ) : null}

              {mfaFlow.step === "setup_totp_confirm" ? (
                <form className="formGrid" onSubmit={confirmTotpSetup}>
                  <div className="muted">
                    Scan this QR code with your authenticator app. If you need
                    to, you can still enter the secret key manually below.
                  </div>
                  <div className="mfaQrPanel">
                    <div className="mfaQrPanel__code" aria-hidden={!totpQrDataUrl}>
                      {totpQrDataUrl ? (
                        <img
                          src={totpQrDataUrl}
                          alt="QR code for BossDrop MFA setup"
                          className="mfaQrPanel__image"
                        />
                      ) : (
                        <div className="mfaQrPanel__placeholder">
                          Building QR code...
                        </div>
                      )}
                    </div>
                    <div className="mfaQrPanel__copy">
                      Works with Google Authenticator, Microsoft Authenticator,
                      Authy, 1Password, and similar apps.
                    </div>
                  </div>
                  <label className="field wide">
                    Secret key
                    <input
                      className="inputFancy"
                      value={mfaFlow.secret || ""}
                      readOnly
                    />
                  </label>
                  <label className="field wide">
                    Account URI
                    <input
                      className="inputFancy"
                      value={mfaFlow.otpAuthUrl || ""}
                      readOnly
                    />
                  </label>
                  <label className="field wide">
                    6-digit code
                    <input
                      className="inputFancy"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      placeholder="123456"
                      value={mfaFlow.code || ""}
                      onChange={(e) =>
                        setMfaFlow((current) => ({
                          ...(current || buildMfaFlowFromPending({ purpose: "setup" })),
                          code: e.target.value.replace(/\D/g, "").slice(0, 6),
                        }))
                      }
                    />
                  </label>
                  <div className="ctaRow">
                    <button className="btn primary large">Turn on MFA</button>
                    <button
                      type="button"
                      className="btn ghost"
                      onClick={() =>
                        setMfaFlow((current) => ({
                          ...(current || buildMfaFlowFromPending({ purpose: "setup" })),
                          step: "setup_choice",
                          method: "",
                          code: "",
                          secret: "",
                          otpAuthUrl: "",
                        }))
                      }
                    >
                      Back
                    </button>
                  </div>
                </form>
              ) : null}

              {mfaFlow.step === "setup_sms_verify" ? (
                <form className="formGrid" onSubmit={confirmSmsSetup}>
                  <div className="muted">
                    Enter the 6-digit code we sent to{" "}
                    <strong>{mfaFlow.maskedPhone || "your phone"}</strong>.
                  </div>
                  <label className="field wide">
                    6-digit code
                    <input
                      className="inputFancy"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      placeholder="123456"
                      value={mfaFlow.code || ""}
                      onChange={(e) =>
                        setMfaFlow((current) => ({
                          ...(current || buildMfaFlowFromPending({ purpose: "setup" })),
                          code: e.target.value.replace(/\D/g, "").slice(0, 6),
                        }))
                      }
                    />
                  </label>
                  <div className="ctaRow">
                    <button className="btn primary large">Turn on MFA</button>
                    <button type="button" className="btn ghost" onClick={resendMfaCode}>
                      Resend code
                    </button>
                  </div>
                </form>
              ) : null}

              {mfaFlow.step === "verify" ? (
                <form className="formGrid" onSubmit={verifyMfa}>
                  <div className="muted">
                    {mfaFlow.method === "sms"
                      ? `Enter the 6-digit code we sent to ${mfaFlow.maskedPhone || "your phone"}.`
                      : "Open your authenticator app and enter the 6-digit code."}
                  </div>
                  <label className="field wide">
                    6-digit code
                    <input
                      className="inputFancy"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      placeholder="123456"
                      value={mfaFlow.code || ""}
                      onChange={(e) =>
                        setMfaFlow((current) => ({
                          ...(current || buildMfaFlowFromPending({ purpose: "verify" })),
                          code: e.target.value.replace(/\D/g, "").slice(0, 6),
                        }))
                      }
                    />
                  </label>
                  <div className="ctaRow">
                    <button className="btn primary large">Verify</button>
                    {mfaFlow.method === "sms" ? (
                      <button
                        type="button"
                        className="btn ghost"
                        onClick={resendMfaCode}
                      >
                        Resend code
                      </button>
                    ) : null}
                  </div>
                </form>
              ) : null}

              <div className="ctaRow">
                <button type="button" className="btn ghost" onClick={startOver}>
                  Start over
                </button>
              </div>
              {status && <div className="muted">{status}</div>}
            </div>
          ) : null}

          {!showMfa && mode === "login" ? (
            <form className="formGrid" onSubmit={doLogin}>
              {notice ? (
                <div className="infoBanner authNotice" role="status">
                  {notice}
                </div>
              ) : null}
              {closedBanner}
              <label className="field wide">
                Email
                <input
                  className="inputFancy"
                  name="email"
                  type="email"
                  placeholder="you@company.com"
                  required
                  value={form.email}
                  onChange={onChange}
                />
              </label>
              <label className="field wide">
                Password
                <input
                  className="inputFancy"
                  name="password"
                  type="password"
                  placeholder="........"
                  required
                  value={form.password}
                  onChange={onChange}
                />
              </label>
              <div className="ctaRow">
                <button className="btn primary large">Sign in</button>
                <button
                  type="button"
                  className="btn ghost"
                  onClick={() => switchMode("forgot")}
                >
                  Forgot?
                </button>
              </div>
              <div className="ctaRow">
                <GoogleBtn />
              </div>
              {status && <div className="muted">{status}</div>}
              {/* Not under the "Password set" notice: whoever just finished an
                  invite already has an account and only needs to sign in. */}
              {!signupsOpen && !notice ? (
                <div className="authFootnote">
                  {/* The signups_closed banner already gives the sales address,
                      so it is not repeated a few lines further down. */}
                  {!closedError ? (
                    <div>
                      BossDrop is invite-only. To get started, email{" "}
                      <SalesLink />.
                    </div>
                  ) : null}
                  <div>
                    {/* Teammates of an existing customer still create their own
                        account: the server lets an address through when it is
                        already listed in that workspace's Team directory. */}
                    <button
                      type="button"
                      className="authLink"
                      onClick={() => {
                        // A leftover "Login failed" (or the Google bounce
                        // banner) should not follow them onto the new form.
                        setStatus("");
                        switchMode("register");
                      }}
                    >
                      Joining your team's workspace? Create your account
                    </button>
                  </div>
                </div>
              ) : null}
            </form>
          ) : null}

          {!showMfa && mode === "register" ? (
            <form className="formGrid" onSubmit={doRegister}>
              {closedBanner}
              {/* The banner says the same thing with more urgency, so the
                  explainer steps aside while it is up. */}
              {!signupsOpen && !closedError ? (
                <div className="muted authLede">
                  Your email has to be listed in your team's Team directory
                  first. If it isn't, ask your workspace admin to add it. We
                  will email you a link to set your password.
                </div>
              ) : null}
              <label className="field wide">
                Name (optional)
                <input
                  className="inputFancy"
                  name="name"
                  placeholder="Your name"
                  value={form.name}
                  onChange={onChange}
                />
              </label>
              <label className="field wide">
                Email
                <input
                  className="inputFancy"
                  name="email"
                  type="email"
                  placeholder="you@company.com"
                  required
                  value={form.email}
                  onChange={onChange}
                />
              </label>
              {/* Closed signups never take a password here: the emailed
                  link is where it is chosen. */}
              {signupsOpen ? (
                <label className="field wide">
                  Password
                  <input
                    className="inputFancy"
                    name="password"
                    type="password"
                    placeholder={`At least ${MIN_PASSWORD_LENGTH} characters`}
                    required
                    value={form.password}
                    onChange={onChange}
                  />
                </label>
              ) : null}
              <div className="ctaRow">
                <button className="btn primary large">
                  {signupsOpen ? "Create account" : "Email me a link"}
                </button>
              </div>
              {status && <div className="muted">{status}</div>}
              {/* Closed signups have no "Create account" tab to show where they
                  are, so the way back gets the same footnote the login form
                  uses. A link, not a second button: two buttons do not fit on
                  one row at phone width and the CTA wrapped to two lines. */}
              {!signupsOpen ? (
                <div className="authFootnote">
                  <div>
                    Already have an account?{" "}
                    <button
                      type="button"
                      className="authLink"
                      onClick={() => {
                        setStatus("");
                        switchMode("login");
                      }}
                    >
                      Sign in
                    </button>
                  </div>
                </div>
              ) : null}
            </form>
          ) : null}

          {!showMfa && mode === "forgot" ? (
            <form className="formGrid" onSubmit={doForgot}>
              <label className="field wide">
                Email
                <input
                  className="inputFancy"
                  name="email"
                  type="email"
                  placeholder="you@company.com"
                  required
                  value={form.email}
                  onChange={onChange}
                />
              </label>
              <div className="ctaRow">
                <button className="btn primary large">Send reset link</button>
                <button
                  type="button"
                  className="btn ghost"
                  onClick={() => switchMode("login")}
                >
                  Back to sign in
                </button>
              </div>
              {status && <div className="muted">{status}</div>}
            </form>
          ) : null}

          {!showMfa && mode === "reset" ? (
            <form className="formGrid" onSubmit={doReset}>
              {/* The form still works: the token decides whose password is set,
                  not the session. This only explains why the page looks odd to
                  someone who is already signed in. */}
              {signedInAs ? (
                <div className="infoBanner authNotice" role="status">
                  {isInviteReset
                    ? `You're signed in as ${signedInAs}. This link sets the password for a different account. To try it as your client would, open it in a private window or sign out first.`
                    : `You're signed in as ${signedInAs}. This link resets the password for the account it was emailed to.`}
                </div>
              ) : null}
              <label className="field wide">
                {isInviteReset ? "Choose a password" : "New password"}
                <input
                  className="inputFancy"
                  name="password"
                  type="password"
                  placeholder={`At least ${MIN_PASSWORD_LENGTH} characters`}
                  required
                  value={form.password}
                  onChange={onChange}
                />
              </label>
              <div className="ctaRow">
                <button className="btn primary large">
                  {isInviteReset ? "Set password" : "Reset password"}
                </button>
              </div>
              {status && <div className="muted">{status}</div>}
            </form>
          ) : null}
        </div>
      </div>
    </div>
  );
}
